// Cave84 — OpenBottleModal (Phase 7)
// Spec §4.1 F1.3 + §4.9 Consumption & Tasting Notes.
// Status: in_cellar -> consumed. NEVER deletes.
// Everything the user fills in here (location, note, wishlist) commits in a
// single write only when they press "Save & Open Bottle" — nothing is
// written on individual field changes.

import { useState } from 'react';
import type { Bottle } from '@cave84/shared-types';
import { useOpenBottle } from '@/hooks/useBottles';
import { useWine } from '@/hooks/useWines';
import { useVintage } from '@/hooks/useVintages';

interface Props {
  bottle: Bottle;
  onClose: () => void;
}

const LOCATION_PRESETS = ['Home', 'Restaurant', "Friend's place", 'Tasting / event'];

export default function OpenBottleModal({ bottle, onClose }: Props) {
  const { data: vintage } = useVintage(bottle.vintage_id);
  const { data: wine } = useWine(vintage?.wine_id);
  const openBottle = useOpenBottle();

  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [addToWishlist, setAddToWishlist] = useState(false);

  async function handleConfirm() {
    await openBottle.mutateAsync({
      bottle,
      wineId: wine?.id,
      location: location.trim() || undefined,
      note: note.trim() || undefined,
      addToWishlist,
    });
    onClose();
  }

  const label = wine && vintage
    ? `${vintage.year} ${wine.cuvee_name}`
    : 'this bottle';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 overflow-y-auto">
      <div className="bg-surface-1 rounded-xl border border-border-medium w-full max-w-md shadow-xl my-auto">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border-subtle">
          <div className="flex justify-center mb-3">
            <span className="text-5xl">🍷</span>
          </div>
          <h2 className="text-2xl font-bold text-text-primary text-center mb-2">Open Bottle</h2>
          <p className="text-base text-text-secondary text-center">
            Marking <span className="font-semibold text-text-primary">{label}</span> as consumed.
          </p>
          <p className="text-sm text-text-muted text-center mt-1">
            It will move to your Consumed Archive and leave your active inventory. This cannot be undone.
          </p>
        </div>

        {/* Fields */}
        <div className="px-6 py-5 space-y-5">
          {/* Location */}
          <div>
            <label htmlFor="open-bottle-location" className="block text-base font-semibold text-text-primary mb-2">
              Where did you drink it?
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {LOCATION_PRESETS.map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setLocation(preset)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    location === preset
                      ? 'border-gold bg-gold/10 text-gold'
                      : 'border-border-medium bg-surface-2 text-text-secondary hover:border-border-strong'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              id="open-bottle-location"
              type="text"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Home, Restaurant Mizlala…"
              className="w-full px-4 py-3 rounded-md border border-border-medium bg-surface-0 text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold"
            />
          </div>

          {/* Drinking notes */}
          <div>
            <label htmlFor="open-bottle-note" className="block text-base font-semibold text-text-primary mb-2">
              Drinking notes <span className="font-normal text-text-muted">(optional)</span>
            </label>
            <textarea
              id="open-bottle-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={4}
              placeholder="How did it taste? Occasion, food pairing, who was there…"
              className="w-full px-4 py-3 rounded-md border border-border-medium bg-surface-0 text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:border-gold resize-none"
            />
          </div>

          {/* Wishlist */}
          <label
            htmlFor="open-bottle-wishlist"
            className={`flex items-center gap-3 px-4 py-3 rounded-md border border-border-medium bg-surface-2 ${
              wine ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <input
              id="open-bottle-wishlist"
              type="checkbox"
              checked={addToWishlist}
              onChange={e => setAddToWishlist(e.target.checked)}
              disabled={!wine}
              className="w-5 h-5 rounded border-border-medium accent-gold flex-shrink-0"
            />
            <span className="text-base text-text-primary">
              Add to Wishlist <span className="text-text-muted">— want to buy again</span>
            </span>
          </label>

          {openBottle.isError && (
            <p className="text-sm text-red-400 text-center">
              {(openBottle.error as Error).message}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={openBottle.isPending}
            className="flex-1 px-4 py-3 rounded-md border border-border-medium bg-surface-2 text-text-primary text-base font-medium hover:bg-surface-3 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={openBottle.isPending}
            className="flex-1 px-4 py-3 rounded-md bg-claret text-bg font-semibold text-base hover:bg-claret/90 transition-colors disabled:opacity-40"
          >
            {openBottle.isPending ? 'Saving…' : 'Save & Open Bottle'}
          </button>
        </div>
      </div>
    </div>
  );
}
